import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getDraftDocumentUrlAPI,
  getDraftOverviewAPI,
  removeDraftDocumentAPI,
  updateDraftProfileAPI,
  uploadDraftDocumentAPI,
} from "../../../utils/api/storeOnboardingDraft";
import { ONBOARDING_TEXT } from "../OnboardingStatus/onboardingText";

/*
  Store onboarding for a client whose store is still being set up (a `new` client): the same store, the same Completion screen and the
  same branches list as every other client, written through the onboarding draft API (/store-onboarding/stores/:storeId/*), which
  honours the review lock (an information review in progress or approved is read-only) and only ever writes profile data and DRAFT
  branches. Nothing here activates a branch, an application or a subscription.

  `enabled` is decided by the caller from the central policy (the client holds `onboarding.draft.edit` for this store but not the
  dashboard completion permission). When it is false nothing is requested.
*/

const tokenOf = () => localStorage.getItem("auth-token");

export default function useOnboardingDraft({ storeId, enabled, lang = "ar", onChanged }) {
  const t = ONBOARDING_TEXT[lang === "en" ? "en" : "ar"];
  const [state, setState] = useState("idle"); // idle | loading | ready | error
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const latest = useRef("");
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;

  const messageFor = useCallback((failure) => t.errors[failure?.code] || failure?.message || t.errorFallback, [t]);

  const load = useCallback(async () => {
    if (!enabled || !storeId || !tokenOf()) return;
    latest.current = storeId;
    setState((current) => (current === "ready" ? current : "loading"));
    try {
      const result = await getDraftOverviewAPI(tokenOf(), storeId);
      if (latest.current !== storeId) return;
      setOverview(result);
      setError("");
      setState("ready");
    } catch (failure) {
      if (latest.current !== storeId) return;
      setError(messageFor(failure));
      setState("error");
    }
  }, [enabled, storeId, messageFor]);

  useEffect(() => {
    setOverview(null);
    setState(enabled && storeId ? "loading" : "idle");
    load();
  }, [load, enabled, storeId]);

  // Runs one write, refreshes both the onboarding state and the dashboard (the store data every section shows), and rethrows a
  // refusal so the section that asked can show the server's message and stay open.
  const write = useCallback(
    async (action) => {
      setBusy(true);
      try {
        const result = await action();
        await Promise.all([load(), Promise.resolve(onChangedRef.current?.())]);
        return result;
      } catch (failure) {
        const wrapped = new Error(messageFor(failure));
        wrapped.code = failure?.code;
        throw wrapped;
      } finally {
        setBusy(false);
      }
    },
    [load, messageFor],
  );

  const actions = useMemo(
    () => ({
      saveProfile: (payload) => write(() => updateDraftProfileAPI(tokenOf(), storeId, payload)),
      uploadDocument: (key, file) => write(() => uploadDraftDocumentAPI(tokenOf(), storeId, key, file)),
      removeDocument: (key) => write(() => removeDraftDocumentAPI(tokenOf(), storeId, key)),
      documentUrl: (key) => getDraftDocumentUrlAPI(tokenOf(), storeId, key),
    }),
    [write, storeId],
  );

  const onboarding = overview?.onboarding || null;
  return {
    enabled: Boolean(enabled && storeId),
    state,
    error,
    busy,
    reload: load,
    onboarding,
    // Editable only while the server says so (new / inProgress / rejected). Until it answered, nothing is editable.
    canEdit: Boolean(onboarding?.canEdit),
    ...actions,
  };
}
